import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, message, Result } from 'antd';
import { LockOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';

const { Title, Paragraph } = Typography;

const ResetPassword = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const t = searchParams.get('token');
    const e = searchParams.get('email');
    if (t) setToken(t);
    if (e) setEmail(e);
    if (t && e) {
      form.setFieldsValue({ email: e });
    }
  }, [searchParams, form]);

  const onFinish = async (values) => {
    if (!token) {
      message.error('Invalid reset link. No token found.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/reset-password', {
        email: values.email,
        token: token,
        password: values.password,
        password_confirmation: values.password_confirmation,
      });
      setSubmitted(true);
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to reset password. Please try again.';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          background: 'linear-gradient(135deg, #2d5a3d 0%, #3d7a4d 30%, #52b788 70%, #7fb069 100%)',
        }}
      >
        <Card
          style={{ maxWidth: 500, borderRadius: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}
        >
          <Result
            status="success"
            title="Password Reset Successfully!"
            subTitle="Your password has been changed. You can now login with your new password."
          />
          <div style={{ textAlign: 'center' }}>
            <Link to="/login">
              <Button type="primary" icon={<ArrowLeftOutlined />}>
                Go to Login
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        background: 'linear-gradient(135deg, #2d5a3d 0%, #3d7a4d 30%, #52b788 70%, #7fb069 100%)',
      }}
    >
      <Card
        style={{
          width: '100%',
          maxWidth: 450,
          borderRadius: 20,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Title level={2} style={{ color: '#2d5a3d', marginBottom: 8 }}>
            Reset Your Password
          </Title>
          <Paragraph style={{ color: '#6b7280' }}>Enter your new password below.</Paragraph>
        </div>
        <Form form={form} onFinish={onFinish} layout="vertical" size="large">
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'Please enter your email!' },
              { type: 'email', message: 'Please enter a valid email!' },
            ]}
          >
            <Input placeholder="Enter your email" disabled={!!email} />
          </Form.Item>
          <Form.Item
            name="password"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter a new password!' },
              { min: 8, message: 'Password must be at least 8 characters!' },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="New password" />
          </Form.Item>
          <Form.Item
            name="password_confirmation"
            label="Confirm New Password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm your new password!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) return Promise.resolve();
                  return Promise.reject(new Error('Passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Confirm new password" />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              style={{
                height: 48,
                borderRadius: 12,
                border: 'none',
                background: 'linear-gradient(135deg, #52b788, #3d7a4d)',
                boxShadow: '0 4px 14px rgba(82, 183, 136, 0.4)',
                fontWeight: 600,
                fontSize: 16,
              }}
            >
              Reset Password
            </Button>
          </Form.Item>
        </Form>
        <div style={{ textAlign: 'center' }}>
          <Link to="/login" style={{ color: '#52b788' }}>
            <ArrowLeftOutlined /> Back to Login
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default ResetPassword;
